import { toTomanParts } from "../lib/money";
import { cx } from "../utils/cx";

const sizes = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-md",
    lg: "text-lg",
    xl: "text-xl",
    "display-xs": "text-display-xs",
    "display-sm": "text-display-sm",
    "display-md": "text-display-md",
} as const;

export interface MoneyProps {
    /** Amount in rial (BigInt or the API's integer string). Shown as toman. */
    rial: bigint | string;
    size?: keyof typeof sizes;
    /** Show the «تومان» unit. Drop it only where a column header carries the unit. */
    unit?: boolean;
    /** Prefix positive amounts with «+». Negative amounts always get «−». */
    signed?: boolean;
    /** Colour by sign: green for positive, red for negative. */
    toneBySign?: boolean;
    className?: string;
    unitClassName?: string;
}

/**
 * Rial in, toman out: «3٬400٬000 تومان» (digits drawn Persian by the font, tabular via ss03).
 * The numeric part is isolated LTR so the minus sign and separators never reorder in RTL text.
 */
export const Money = ({ rial, size = "md", unit = true, signed = false, toneBySign = false, className, unitClassName }: MoneyProps) => {
    const { sign, grouped } = toTomanParts(rial);
    const prefix = sign < 0 ? "−" : signed && sign > 0 ? "+" : "";

    return (
        <span
            className={cx(
                "inline-flex items-baseline gap-1 whitespace-nowrap digits-tabular",
                sizes[size],
                toneBySign && sign > 0 && "text-success-primary",
                toneBySign && sign < 0 && "text-error-primary",
                className,
            )}
        >
            <bdi dir="ltr">
                {prefix}
                {grouped}
            </bdi>
            {unit && <span className={cx("text-[0.72em] font-medium text-tertiary", unitClassName)}>تومان</span>}
        </span>
    );
};
